<template>
  <v-card class="chartdetailsviewer d-flex flex-column mb-6" height="100%" flat>
    <v-card-item id="chart_details_title" :title="currentSeriesName"> </v-card-item>

    <div id="listContainer" class="list-container">
      <analyze-data-table
        v-if="computedTableOptions.headers && computedTableOptions.items"
        table-id="details-table-id"
        :headers="computedTableOptions.headers"
        :fixed-header="true"
        :fixed-footer="true"
        :items="computedTableOptions.items"
        :has-multisort="true"
        :max-height="85"
        :page="serverOptions.page"
        :items-per-page="serverOptions.rowsPerPage"
        :total-items="totalItems"
        :load-items="loadDetailsFromServer"
        :loading="loading"
        :has-server-sided-pagination="true"
        @select="navigateToArtifact"
      />
    </div>
  </v-card>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import labels from '@/translations/labels.json'
  import { ChartDetails } from '@/models/dashboard.model'
  import { Project, Snapshot } from '@/models'
  import { Chart } from '@/models/chart.model'
  import { escapeRegExp } from 'lodash'
  import { useRoute, useRouter } from 'vue-router'
  import { useProjectStore } from '@/stores/projectStore'
  import { useSnapshotStore } from '@/stores/snapshotStore'
  import { useRouteStore } from '@/stores/routeStore'
  import { useTraceDataStore } from '@/stores/traceDataStore'
  import { AnalyzeTableHeader, AnalyzeTableItem } from '@/models/table.model'
  import AnalyzeDataTable from '@/components/AnalyzeDataTable/AnalyzeDataTable.vue'
  import { injectTyped } from '@/inject'
  import { ServiceKeys } from '@/services/symbols'

  const projectStore = useProjectStore()
  const snapshotStore = useSnapshotStore()
  const routeStore = useRouteStore()
  const traceDataStore = useTraceDataStore()
  const router = useRouter()

  const dashboardService = injectTyped(ServiceKeys.dashboard)

  const currentProject = computed((): Project | undefined => {
    return projectStore.currentProject
  })

  const currentSnapshot = computed((): Snapshot | undefined => {
    return snapshotStore.currentSnapshot
  })

  const detailsHeaders = ref<AnalyzeTableHeader[]>([])
  const detailsItems = ref<any[]>([])
  const loading = ref(true)
  const totalItems = computed(() => {
    return totalServerItemsLength.value
  })

  const currentChartDetailsPath = computed((): string | undefined => {
    return routeStore.path
  })

  const currentSeriesName = computed((): string | undefined => {
    const route = useRoute()
    return route.params.seriesName as string
  })

  const totalServerItemsLength = computed((): number => {
    const route = useRoute()
    return +route.params.totalServerItemsLength as number
  })

  const serverOptions = ref<{ page: number; rowsPerPage: number }>({
    page: 1,
    rowsPerPage: 25,
  })

  const computedTableOptions = computed(() => {
    let headers: AnalyzeTableHeader[] = []
    let items: AnalyzeTableItem<any>[] = []

    headers = detailsHeaders.value
    items = detailsItems.value
    return { headers, items }
  })

  function getCompleteChartDetailsPath(): string {
    const originalPath = currentChartDetailsPath.value
    let finalPath: string[] = []
    if (originalPath) {
      const pathAfterReplacing = originalPath.replace(new RegExp(escapeRegExp('%2F'), 'g'), '/')
      finalPath = pathAfterReplacing.split('/details')
    }
    return finalPath[0] + '/details'
  }

  function navigateToArtifact(tableRow: AnalyzeTableItem<any>): void {
    const artifactId = tableRow.id
    traceDataStore.setArtifactId(artifactId)
    router.push({
      name: labels.SIDEBAR.MAIN.EXPLORER_GRAPH,
      params: {
        projectId: currentProject.value?.id.toString(),
        snapshotId: currentSnapshot.value?.id.toString(),
        artifactId: artifactId,
      },
      query: {
        filter: routeStore.filterId,
      },
    })
    routeStore.setArtifactId(artifactId)
  }

  const loadDetailsFromServer = async (options: {
    page: number
    itemsPerPage: number
    sortBy?: string[]
    sortDesc?: boolean[]
  }) => {
    const { page, itemsPerPage } = options
    loading.value = true

    detailsItems.value = []
    detailsHeaders.value = []

    serverOptions.value.page = page
    serverOptions.value.rowsPerPage = itemsPerPage
    if (
      routeStore.chartDetailsPath !== undefined &&
      routeStore.chartDetailsPath !== '' &&
      currentSeriesName.value
    ) {
      const chart: Chart = {
        id: '',
        defaultTitle: currentSeriesName.value,
        defaultSubTitle: '',
        path: getCompleteChartDetailsPath(),
        detailsPath: getCompleteChartDetailsPath(),
        chartType: 'pie',
        series: [],
        color: [],
        labelsChart: [],
      }
      if (dashboardService != undefined) {
        dashboardService
          .getChartDetailsData(chart, currentSeriesName.value, serverOptions.value)
          .then((data: ChartDetails) => {
            const headers = dashboardService.getChartDetailsHeaders(data)
            detailsItems.value = dashboardService.getChartDetailsItems(data)
            detailsHeaders.value = headers
            loading.value = false
            setTimeout(() => {
              loading.value = false
            }, 1000)
          })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .chartdetailsviewer {
    height: 100%;
    overflow: auto;
    background-color: transparent;
  }

  .artifact-name {
    color: rgb(var(--v-theme-font-color));
    font-weight: bolder;
  }
</style>
